import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import { App } from './App';
import { initSentry, BUILD_TAG, SENTRY_ENABLED, breadcrumb } from './lib/sentry';
import { initAnalytics } from './lib/analytics';
import { installChunkReload } from './lib/chunkReload';
import { applyLangToDocument, commonT, getLang } from './i18n';

// Sentry primeiro — qualquer erro a partir daqui já é capturado.
initSentry();

// Idioma resolvido uma vez, antes de qualquer tela (ver i18n/core.ts). A tag
// no Sentry separa "quebra só em inglês" de "quebra pra todo mundo"; são três
// valores, sem risco de cardinalidade.
applyLangToDocument(commonT().appTitle);
Sentry.setTag('lang', getLang());

// GA4 de audiência. Só sobe em rota pública de host de produção (ver
// lib/analytics.ts); em dev e no painel do organizador é no-op.
initAnalytics();

// Recuperação de stale-deploy: quando o organizer está com um index.html de um
// build antigo e navega pra uma rota lazy cujo chunk já foi sobrescrito pelo
// deploy seguinte, o Vite dispara `vite:preloadError` → recarregamos uma vez em
// vez de mostrar "Algo deu errado". Ver lib/chunkReload.ts.
installChunkReload();

// Log da release em runtime — útil pra oncall confirmar qual bundle o
// organizer está executando (pode estar com cache stale de bundle antigo).
console.info(`[faromaps-admin] build=${BUILD_TAG} sentry=${SENTRY_ENABLED ? 'on' : 'off'}`);

// Promise rejection — ErrorBoundary não pega isso. Sem o handler, falhas
// async (ex: SignalR rejoin) viram "Uncaught (in promise)" no console e
// somem. Promove a evento Sentry.
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason ?? new Error('unhandledrejection'), {
    tags: { source: 'unhandledrejection' },
  });
  breadcrumb('unhandled promise rejection', 'global', {
    reason: String(event.reason),
  });
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
